/**
 * Site-scoped authorization, resolved by the server.
 *
 * BMS-spec `docs/edge/rest-api.md` §"Current-user permission projection" and
 * `schemas/current-user.v1.schema.json`.
 *
 * This module replaces the hardcoded role lists that used to stand in for a
 * permission read (`canWriteAlarms`, and `canOperateLocks` duplicated in two
 * places). Those lists were transcriptions of `schema.sql`'s seeded grants:
 * correct on the day they were written and silently wrong the moment a site
 * regranted a permission in the database. Authorization is the server's answer,
 * and a client reads it — it does not re-derive it from a role name.
 *
 * Two rules are load-bearing here:
 *
 * 1. **A projection is only evidence for the scope it was fetched for.** Every
 *    read takes the site the caller is asking about and refuses to answer from
 *    another site's snapshot. "Neither an unavailable projection nor a previous
 *    site's snapshot is a grant."
 * 2. **`permissions` is not the whole condition on an operation.** The Field
 *    Service prohibition list below is unconditional, and role- or item-level
 *    rules (card-type issuance, per-param ACL) remain separate checks.
 */
// =============================================================================
// Permission codes this client gates on
// =============================================================================
/**
 * The codes the UI actually checks. This is a convenience vocabulary, not an
 * authority: an unlisted code is still a perfectly valid argument to
 * `hasPermissionAt`, and the server remains the only thing that decides.
 */
export const PERMISSIONS = {
    /** Acknowledge and Resolve on alarms — GRMS specification §9.3. */
    ALARMS_WRITE: 'alarms:write',
    ALARMS_READ: 'alarms:read',
    /** Every manual lock action: Relock, held Unlock, momentary Open. */
    ACCESS_CONTROL_OPERATE: 'access_control:operate',
    ACCESS_CONTROL_READ: 'access_control:read',
    HOUSEKEEPING_WRITE: 'housekeeping:write',
    /**
     * Mark inspected and Fail — the two edges out of `to_inspect` (GRMS §11.4).
     * `front_office_manager` holds it without `housekeeping:write`.
     */
    HOUSEKEEPING_INSPECT: 'housekeeping:inspect',
    /**
     * Assign an attendant to rooms, one by one or in a batch (GRMS §11.4). The
     * attendant, who holds `housekeeping:write`, does not hold it; holding write
     * without it is what puts the User UI in attendant mode.
     */
    HOUSEKEEPING_ASSIGN: 'housekeeping:assign',
    ROOMS_READ: 'rooms:read',
    // The front-office supervisor surfaces of GRMS §14.2. Each surface is gated
    // on a code rather than on `front_office_manager`, so a site that regrants a
    // code moves the surface with it and `site_admin` — which holds all of them —
    // sees every surface without being named anywhere.
    /** The Cards page: site-wide inventory, search, card detail with events. */
    CARDS_READ: 'cards:read',
    /** Replace a lost or damaged card; guest and temporary issuing. */
    CARDS_WRITE: 'cards:write',
    /** Issue a staff card from a role template; never implied by `cards:write`. */
    CARDS_WRITE_STAFF: 'cards:write_staff',
    /** Report lost, revoke with a stated reason, suspend, reactivate. */
    CARDS_OPERATE: 'cards:operate',
    VISITORS_READ: 'visitors:read',
    VISITORS_WRITE: 'visitors:write',
    /** GRMS reads, including the hotel KPIs behind the Reports page (§11.6). */
    GRMS_READ: 'grms:read',
    /**
     * Occupancy writes — check-in, check-out, stay edit and move (GRMS §11.2).
     * The desk's Check in / Check out and the walk-in "New guest?" path.
     */
    GRMS_WRITE: 'grms:write',
    ACCESS_ANALYTICS_READ: 'access_analytics:read',
    /**
     * Hotel settings › Access hours. Deliberately narrower than
     * `access_control:write`: it replaces the windows of an existing schedule and
     * reaches no other field, no create, no delete and no attachment.
     */
    ACCESS_CONTROL_WRITE_SCHEDULE_WINDOWS: 'access_control:write_schedule_windows',
    /**
     * Hotel settings › Occupancy automation. Narrower than `site:write`: it
     * changes setpoints and scene codes, never `enabled` and never any other
     * site setting.
     */
    SITE_WRITE_OCCUPANCY_AUTOMATION: 'site:write_occupancy_automation',
};
// =============================================================================
// The Field Service prohibition list
// =============================================================================
/**
 * BMS-spec `docs/edge/field-service-identity.md` §10.
 *
 * These are refused for a federated principal **regardless of any permission it
 * resolves to**, and the API refuses them itself with
 * `403 federated_principal_forbidden`. They are modelled as named operations
 * rather than as permission codes to subtract, because most of them cannot be
 * expressed as the absence of one grant:
 *
 * - `detailedHealth` is a *surface*, not an operation with its own permission;
 *   the coarse public health split stays available to the same principal.
 * - `userAdministration` covers every mutation of every `users` row, including
 *   the federated principal's own — one prohibition over a whole route family
 *   that `users:write` alone does not describe.
 * - `deviceCredentials` covers enrolment and revocation for *any* account,
 *   while the same principal may still read its own credential list.
 *
 * Subtracting a permission would also be a lie about what the server resolved:
 * the projection reports RBAC truthfully and this list is applied on top.
 */
export const FEDERATED_PROHIBITIONS = [
    'vendorAccess',
    'breakGlass',
    'trustAnchor',
    'fieldServiceCertificate',
    'timeRecovery',
    'detailedHealth',
    'userAdministration',
    'deviceCredentials',
];
/**
 * Whether this identity may never perform `operation`, whatever it was granted.
 *
 * A client uses this to withhold the control. It is not the enforcement point —
 * the server refuses these regardless — but offering a control that is
 * guaranteed to be refused is its own defect.
 */
export function isProhibitedForIdentity(operation, identitySource) {
    // Every listed operation is prohibited for a federated principal, so the
    // answer does not currently vary by operation. It is still taken by name:
    // §10 is a list of specific controls, and a caller that has to name one is a
    // caller that cannot accidentally hide an unrelated control by passing a
    // truthy value. An operation outside the list is not prohibited here — it is
    // simply not one of §10's controls.
    if (!FEDERATED_PROHIBITIONS.includes(operation))
        return false;
    return identitySource === 'federated';
}
// =============================================================================
// Reading a projection
// =============================================================================
/**
 * Whether `projection` is evidence about `siteCode` for `identityId`.
 *
 * A projection fetched for site A says nothing about site B, and one fetched
 * for another identity says nothing at all. `siteCode: null` means the read was
 * global-scope only: it answers questions about global grants, and it is not an
 * answer for any particular site.
 */
export function projectionCoversScope(projection, siteCode, identityId) {
    if (!projection)
        return false;
    if (identityId != null && projection.id !== identityId)
        return false;
    return projection.siteCode === (siteCode ?? null);
}
/**
 * The grants that apply at `siteCode`, or `null` when this projection cannot
 * answer for that scope.
 *
 * `null` is deliberately distinct from `[]`: "no grants here" and "I do not
 * know" must not collapse, because only the first is a truthful basis for
 * showing a disabled control with a permission explanation.
 */
export function grantsAt(projection, siteCode, identityId) {
    if (!projectionCoversScope(projection, siteCode, identityId))
        return null;
    return projection.permissions;
}
/**
 * Whether the caller holds `permission` at `siteCode`.
 *
 * Fails closed: an absent, stale, wrong-identity or wrong-site projection is
 * not a grant.
 */
export function hasPermissionAt(projection, siteCode, permission, identityId) {
    return grantsAt(projection, siteCode, identityId)?.includes(permission) ?? false;
}
/** Acknowledge/Resolve on alarms at this site. */
export function canWriteAlarms(projection, siteCode) {
    return hasPermissionAt(projection, siteCode, PERMISSIONS.ALARMS_WRITE);
}
/**
 * Relock, held Unlock and momentary Open at this site.
 *
 * One permission covers all three actions; which of them a given lock accepts
 * is its `lock_mode`, not a permission question.
 */
export function canOperateLocks(projection, siteCode) {
    return hasPermissionAt(projection, siteCode, PERMISSIONS.ACCESS_CONTROL_OPERATE);
}
/**
 * The cache identity of one projection scope.
 *
 * Keyed by identity *and* site so a site switch cannot read the previous site's
 * answer, and a different account cannot read the previous account's.
 *
 * Global scope is the literal `null`, not a sentinel string: `global` matches
 * the site-code grammar `^[a-z0-9-]{1,64}$`, so a site actually named `global`
 * would otherwise share a cache entry with global scope and read its grants.
 */
export function permissionScopeKey(identityId, siteCode) {
    return ['auth', 'me', identityId ?? 'anonymous', siteCode ?? null];
}
//# sourceMappingURL=permissions.js.map